import { useState, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Upload, Download, FileText, X, CheckCircle2, AlertCircle } from "lucide-react";
import { shotsApi } from "@/lib/api";
import { toast } from "sonner";

export const SHOT_CSV_COLUMNS = [
  "scene",
  "shot_number",
  "description",
  "shot_size",
  "camera_angle",
  "lens",
  "location",
  "talent",
  "props",
  "duration",
  "status",
  "scheduled_date",
  "notes",
];

const STATUS_MAP = {
  "not shot": "not_shot",
  "not_shot": "not_shot",
  "notshot": "not_shot",
  "todo": "not_shot",
  "": "not_shot",
  "in progress": "in_progress",
  "in_progress": "in_progress",
  "inprogress": "in_progress",
  "wip": "in_progress",
  "completed": "completed",
  "complete": "completed",
  "done": "completed",
};

// Flexible date normalizer — accepts the most common shapes a client will
// type into a spreadsheet and returns ISO YYYY-MM-DD. Returns null when we
// can't confidently parse the input (importer then surfaces a warning row).
//
// Accepted:
//   7/3/26          → 2026-07-03  (US M/D/YY, 2-digit year, assumes 20xx)
//   07/03/2026      → 2026-07-03  (US M/D/YYYY)
//   7-3-2026        → 2026-07-03  (dashes)
//   2026-07-03      → 2026-07-03  (already ISO)
//   2026/07/03      → 2026-07-03  (ISO with slashes)
//   Jul 3, 2026     → 2026-07-03  (natural language, via Date parse fallback)
//
// Ambiguous DD/MM/YY vs MM/DD/YY is resolved in favor of MM/DD (US convention)
// which matches the app's existing date-picker locale + current userbase.
export function normalizeDate(raw) {
  const s = String(raw || "").trim();
  if (!s) return "";
  // Already ISO?  YYYY-MM-DD
  const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(s);
  if (iso) {
    const [, y, m, d] = iso;
    return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
  }
  // Slash- or dash-separated numeric.
  const parts = s.split(/[/\-.]/).map((p) => p.trim());
  if (parts.length === 3 && parts.every((p) => /^\d+$/.test(p))) {
    let [a, b, c] = parts.map(Number);
    // If the first component is a 4-digit year, treat as YYYY/M/D.
    if (parts[0].length === 4) {
      return `${a}-${String(b).padStart(2, "0")}-${String(c).padStart(2, "0")}`;
    }
    // Otherwise assume US convention M/D/YY(YY).
    let year = c;
    if (parts[2].length === 2) year = 2000 + c;
    if (year < 100) year = 2000 + year;
    if (a < 1 || a > 12 || b < 1 || b > 31) return null;
    return `${year}-${String(a).padStart(2, "0")}-${String(b).padStart(2, "0")}`;
  }
  // Last resort — let the browser try. Handles "Jul 3, 2026" etc.
  const parsed = new Date(s);
  if (!isNaN(parsed.getTime())) {
    const y = parsed.getFullYear();
    const m = String(parsed.getMonth() + 1).padStart(2, "0");
    const d = String(parsed.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }
  return null;
}

// Robust mini CSV parser (handles quoted fields, embedded commas, escaped quotes, CRLF)
export function parseCSV(text) {
  const rows = [];
  let row = [];
  let field = "";
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += ch;
      }
    } else {
      if (ch === '"') {
        inQuotes = true;
      } else if (ch === ",") {
        row.push(field);
        field = "";
      } else if (ch === "\n" || ch === "\r") {
        if (ch === "\r" && text[i + 1] === "\n") i++;
        row.push(field);
        rows.push(row);
        row = [];
        field = "";
      } else {
        field += ch;
      }
    }
  }
  // Last field
  if (field.length > 0 || row.length > 0) {
    row.push(field);
    rows.push(row);
  }
  return rows.filter((r) => r.some((c) => String(c).trim() !== ""));
}

export function buildTemplateCSV() {
  const header = SHOT_CSV_COLUMNS.join(",");
  const sample = [
    [
      "1",
      "1",
      "Detective steps into the dim warehouse, scanning the shadows.",
      "Wide",
      "Low Angle",
      "24mm",
      "Warehouse · Bay 4",
      "Detective Vega",
      "Flashlight, badge",
      "8s",
      "not_shot",
      "2026-06-15",
      "Handheld; smoke FX on",
    ],
    [
      "1",
      "2",
      "Close-up on her hand pulling back the tarp.",
      "Close-Up",
      "Eye Level",
      "50mm",
      "Warehouse · Bay 4",
      "Detective Vega",
      "Tarp, prop crate",
      "4s",
      "in_progress",
      "2026-06-15",
      "Practical light from flashlight only",
    ],
  ];
  const esc = (v) => {
    const s = String(v ?? "");
    if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  return [header, ...sample.map((r) => r.map(esc).join(","))].join("\n");
}

export default function CsvImportDialog({ open, onOpenChange, projectId, onImported }) {
  const fileRef = useRef();
  const [parseResult, setParseResult] = useState(null); // {rows, errors, fileName}
  const [importing, setImporting] = useState(false);

  const reset = () => {
    setParseResult(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleClose = (o) => {
    if (!o) reset();
    onOpenChange(o);
  };

  const handleDownloadTemplate = () => {
    const csv = buildTemplateCSV();
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "shot_list_template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleFile = async (file) => {
    if (!file) return;
    if (!/\.csv$/i.test(file.name)) {
      toast.error("Please upload a .csv file");
      return;
    }
    const text = await file.text();
    const rows = parseCSV(text);
    if (rows.length < 2) {
      setParseResult({ rows: [], errors: ["File appears empty"], fileName: file.name });
      return;
    }
    const rawHeaders = rows[0].map((h) => h.trim().toLowerCase().replace(/\s+/g, "_"));
    const knownCols = new Set(SHOT_CSV_COLUMNS);
    const headerIdx = {};
    rawHeaders.forEach((h, i) => {
      if (knownCols.has(h)) headerIdx[h] = i;
    });
    const errors = [];
    if (Object.keys(headerIdx).length === 0) {
      errors.push("No recognized columns found. Use the template as a reference.");
    }
    const parsed = [];
    for (let r = 1; r < rows.length; r++) {
      const row = rows[r];
      const obj = {};
      SHOT_CSV_COLUMNS.forEach((col) => {
        if (headerIdx[col] !== undefined) {
          obj[col] = (row[headerIdx[col]] || "").trim();
        } else {
          obj[col] = "";
        }
      });
      // Normalize status
      const sNorm = (obj.status || "").trim().toLowerCase();
      obj.status = STATUS_MAP[sNorm] || "not_shot";
      // Normalize scheduled_date — accept M/D/YY, MM/DD/YYYY, YYYY-MM-DD, etc.
      // Bad input becomes an empty date + a per-row warning so the client can
      // fix it later without blocking the whole import.
      if (obj.scheduled_date) {
        const raw = obj.scheduled_date;
        const iso = normalizeDate(raw);
        if (iso === null) {
          errors.push(`Row ${r + 1}: skipped invalid date "${raw}"`);
          obj.scheduled_date = "";
        } else {
          obj.scheduled_date = iso;
        }
      }
      // Skip fully empty rows
      const hasAny = SHOT_CSV_COLUMNS.some(
        (c) => c !== "status" && (obj[c] || "").trim() !== ""
      );
      if (hasAny) parsed.push(obj);
    }
    setParseResult({
      rows: parsed,
      errors,
      fileName: file.name,
      headers: rawHeaders,
    });
  };

  const handleImport = async () => {
    if (!parseResult || parseResult.rows.length === 0) return;
    setImporting(true);
    try {
      const res = await shotsApi.bulkCreate(projectId, parseResult.rows);
      toast.success(`Imported ${res.count} shots`);
      onImported && onImported();
      reset();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-light">
            Import Shots from CSV
          </DialogTitle>
          <DialogDescription className="text-zinc-500">
            Upload a CSV to add shots in bulk. Use the template for the
            expected column structure.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Template download */}
          <div className="flex items-center justify-between gap-4 p-3 rounded-sm border border-zinc-800 bg-zinc-900/40">
            <div className="flex items-center gap-3">
              <FileText className="w-4 h-4 text-amber-500 shrink-0" />
              <div>
                <div className="text-sm text-zinc-200">CSV Template</div>
                <div className="text-xs text-zinc-500">
                  {SHOT_CSV_COLUMNS.join(", ")}
                </div>
              </div>
            </div>
            <button
              data-testid="download-csv-template-btn"
              onClick={handleDownloadTemplate}
              className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-zinc-900 border border-zinc-800 text-zinc-100 hover:border-amber-500/40 hover:text-amber-500 rounded-sm transition-colors"
            >
              <Download className="w-3.5 h-3.5" /> Template
            </button>
          </div>

          {/* Dropzone */}
          {!parseResult ? (
            <div
              data-testid="csv-dropzone"
              onClick={() => fileRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={onDrop}
              className="border border-dashed border-zinc-800 rounded-md py-10 px-6 text-center cursor-pointer hover:border-amber-500/40 hover:bg-zinc-900/40 transition-colors"
            >
              <Upload className="w-6 h-6 text-zinc-500 mx-auto mb-3" />
              <div className="text-sm text-zinc-300 font-medium">
                Click or drop a CSV file
              </div>
              <div className="text-xs text-zinc-500 mt-1">
                The first row must contain column headers
              </div>
              <input
                ref={fileRef}
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => handleFile(e.target.files?.[0])}
                className="hidden"
                data-testid="csv-file-input"
              />
            </div>
          ) : (
            <div className="rounded-md border border-zinc-800 bg-zinc-900/40 p-4 space-y-3">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 min-w-0">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                  <div className="min-w-0">
                    <div className="text-sm text-zinc-100 truncate">
                      {parseResult.fileName}
                    </div>
                    <div className="text-xs text-zinc-500">
                      {parseResult.rows.length} shot
                      {parseResult.rows.length === 1 ? "" : "s"} ready to import
                    </div>
                  </div>
                </div>
                <button
                  data-testid="clear-csv-btn"
                  onClick={reset}
                  className="p-1.5 rounded-sm text-zinc-500 hover:text-zinc-100 hover:bg-zinc-800"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {parseResult.errors.length > 0 && (
                <div className="flex items-start gap-2 text-xs text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded-sm p-2">
                  <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                  <div>{parseResult.errors.join(" · ")}</div>
                </div>
              )}

              {/* Preview */}
              {parseResult.rows.length > 0 && (
                <div className="border border-zinc-800 rounded-sm overflow-hidden">
                  <div className="max-h-56 overflow-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-zinc-950 sticky top-0">
                        <tr>
                          <th className="px-2 py-2 text-left text-zinc-500 font-medium">#</th>
                          <th className="px-2 py-2 text-left text-zinc-500 font-medium">Scene</th>
                          <th className="px-2 py-2 text-left text-zinc-500 font-medium">Shot</th>
                          <th className="px-2 py-2 text-left text-zinc-500 font-medium">Description</th>
                          <th className="px-2 py-2 text-left text-zinc-500 font-medium">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {parseResult.rows.slice(0, 50).map((r, idx) => (
                          <tr key={idx} className="border-t border-zinc-800/60">
                            <td className="px-2 py-1.5 text-zinc-500 font-mono">{idx + 1}</td>
                            <td className="px-2 py-1.5 text-zinc-300">{r.scene || "—"}</td>
                            <td className="px-2 py-1.5 text-zinc-300">{r.shot_number || "—"}</td>
                            <td className="px-2 py-1.5 text-zinc-200 truncate max-w-[260px]">
                              {r.description || <span className="text-zinc-600 italic">—</span>}
                            </td>
                            <td className="px-2 py-1.5 text-zinc-400">{r.status}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {parseResult.rows.length > 50 && (
                    <div className="px-2 py-1.5 text-xs text-zinc-500 border-t border-zinc-800 bg-zinc-950">
                      …and {parseResult.rows.length - 50} more
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="pt-2">
          <Button
            variant="outline"
            onClick={() => handleClose(false)}
            className="bg-transparent border-zinc-800 text-zinc-300 hover:bg-zinc-900 hover:text-zinc-100"
          >
            Cancel
          </Button>
          <Button
            data-testid="confirm-csv-import-btn"
            onClick={handleImport}
            disabled={!parseResult || parseResult.rows.length === 0 || importing}
            className="bg-amber-500 text-black hover:bg-amber-400 rounded-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {importing
              ? "Importing…"
              : parseResult
                ? `Import ${parseResult.rows.length} shots`
                : "Import"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
